// EditKundenUsersPage.jsx
import React from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar,
  Checkbox,
  FormControlLabel,
  Divider,
  Fab,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  useForm,
  FormProvider,
  Controller,
  useFieldArray,
  useWatch,
  useFormContext,
} from "react-hook-form";
import { ReactComponent as BackSvg } from "../../assets/BackSvg.svg";

// SVGs (kept as React components to match your project setup)
import { ReactComponent as DeltaTechSvg } from "../../assets/DeltaTech.svg";
import { ReactComponent as DeleteOutlineIcon } from "../../assets/DeleteSvg.svg";
import { ReactComponent as UploadFileSvg } from "../../assets/UploadFile.svg";
import { ReactComponent as FileUploadPlaceholderSvg } from "../../assets/FileUploadPlaceholder.svg";
import GoBack from "../../components/GoBack";

/* -------------------- Dummies (replace with API later) -------------------- */
const DEFAULT_USERS = [
  {
    firstName: "Susanne",
    lastName: "Laufner",
    email: "susanne.laufner@example.com",
    status: "aktiviert",
    mobile: "+49 171 2345678",
    phone: "+49 711 123456",
    autoInvite: false,
    avatar: "",
  },
];

const makeDefaultUser = () => ({
  firstName: "",
  lastName: "",
  email: "",
  status: "aktiviert",
  mobile: "",
  phone: "",
  autoInvite: false,
  avatar: "",
});

/* ========================= Page (standalone, no stepper) ========================= */
const EditKundenUsersPage = () => {
  const methods = useForm({
    mode: "onChange",
    defaultValues: { users: DEFAULT_USERS },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "users",
  });

  const [expandedIndex, setExpandedIndex] = React.useState(fields.length ? 0 : -1);

  const handleAddUser = () => {
    const next = fields.length;
    append(makeDefaultUser());
    setExpandedIndex(next);
  };

  const onSaveAll = (data) => {
    // TODO: wire up to API
    // eslint-disable-next-line no-console
    console.log("SAVE users:", data.users);
  };

  return (
    <Box sx={{ p: 2 }} className="editKunkenUsersPage">

      <GoBack  icon={<BackSvg />} text={"Kunde DELTA Tech"} color="rgba(0, 0, 0, 0.50)" to={"/kunden-detail"} />
      <br />
      <br />

      <FormProvider {...methods}>
        <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: "#E6E8EB" }}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 1.5 }}>
            <Typography className="textSecondary" sx={{ fontWeight: 500, mb: 2 }}>
              <DeltaTechSvg /> {"Company Name"}
            </Typography>

            <Typography variant="h6"  className="fRubik"  sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
              User verwalten
            </Typography>
            <Typography
              className="textSecondary"
              sx={{ color: "rgba(var(--color-primary-rgb), 0.65)", mt: 1, mb: 3, width: "80%" }}
            >
              Ein Kunde kann mehrere Userinnen und User haben. Bitte beachten Sie, dass
              Sie die User nicht jetzt definieren müssen und dies zu einem späteren
              Zeitpunkt machen können, sobald Sie Ihre Kunden zur Zusammenarbeit
              einladen wollen.
            </Typography>
          </Box>

          {/* Existing users (editable accordions) */}
          <Box sx={{ px: 3, pb: 1 }}>
            {fields.map((f, idx) => (
              <UserRow
                key={f.id}
                index={idx}
                onRemove={() => {
                  remove(idx);
                  setExpandedIndex((prev) => (prev > idx ? prev - 1 : prev === idx ? -1 : prev));
                }}
                expanded={expandedIndex === idx}
                setExpanded={(open) => setExpandedIndex(open ? idx : -1)}
              />
            ))}
          </Box>

          {/* New person */}
          <Box sx={{ px: 3, pb: 3, mt: 4 }}>
            <Button variant="outlined" onClick={handleAddUser}>
              + USER HINZUFÜGEN
            </Button>
          </Box>
        </Paper>
      </FormProvider>

      {/* Footer actions */}
      <Box sx={{ py: 2, borderTop: "1px solid #E6E8EB" }}>
        <Button
          variant="contained"
          onClick={handleSubmit(onSaveAll)}
          disabled={!isValid}
          sx={{ bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "#0b3140" } }}
        >
          ÄNDERUNGEN SPEICHERN
        </Button>
      </Box>
    </Box>
  );
};

export default EditKundenUsersPage;

/* ========================= Row (two-column: fields + avatar) ========================= */
function UserRow({ index, onRemove, expanded, setExpanded }) {
  const { control, setValue } = useFormContext();

  const first = useWatch({ control, name: `users.${index}.firstName` }) || "";
  const last = useWatch({ control, name: `users.${index}.lastName` }) || "";
  const avatarSrc = useWatch({ control, name: `users.${index}.avatar` }) || "";

  const title = `${first} ${last}`.trim() || "Neue Person";

  const onAvatarPick = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setValue(`users.${index}.avatar`, e.target?.result || "", {
        shouldDirty: true,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => setExpanded(open)}
      disableGutters
      sx={{
        mb: 2,
        border: "1px solid #E6E8EB",
        borderRadius: 2,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Typography sx={{ fontWeight: 500, color: "var(--color-primary)" }}>{title}</Typography>

          <IconButton
            aria-label="Entfernen"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT: fields (9 cols) */}
          <Grid item xs={12} md={9} mt={2}>
            <Grid container spacing={4}>
              {/* Vorname / Nachname */}
              <Grid item xs={12} md={6}>
                <Controller
                  name={`users.${index}.firstName`}
                  control={control}
                  rules={{ required: "Pflichtfeld" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Vorname"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`users.${index}.lastName`}
                  control={control}
                  rules={{ required: "Pflichtfeld" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Nachname"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>

              {/* E-Mail / Status */}
              <Grid item xs={12} md={6}>
                <Controller
                  name={`users.${index}.email`}
                  control={control}
                  rules={{
                    required: "Pflichtfeld",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Ungültige E-Mail-Adresse",
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="E-Mail-Adresse"
                      type="email"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`users.${index}.status`}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select fullWidth label="User-Account-Status">
                      <MenuItem value="aktiviert">aktiviert</MenuItem>
                      <MenuItem value="deaktiviert">deaktiviert</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              {/* Handy / Festnetz */}
              <Grid item xs={12} md={6}>
                <Controller
                  name={`users.${index}.mobile`}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Handynummer" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`users.${index}.phone`}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Festnetznummer (optional)" />
                  )}
                />
              </Grid>

              {/* Invite section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" className="fRubik" color={"var(--color-primary)"} sx={{ mb: 0.5 }}>
                  Einladungslink senden
                </Typography>
                <Typography className="textSecondary" color="text.secondary" sx={{ mb: 1 }}>
                  Senden Sie dieser Person eine E-Mail mit einem Einladungslink zur Mitarbeit.
                  Beim ersten Login vergibt die eingeladene Person ihr Passwort selbstständig.
                  Der Einladungslink kann jederzeit gesendet werden.
                </Typography>
                <Controller
                  name={`users.${index}.autoInvite`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label="Einladungslink automatisch versenden nach Erstellung"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* RIGHT: avatar upload (3 cols) */}
          <Grid item xs={12} md={3}>
            <Box sx={{ position: "relative", display: "grid", placeItems: "center", height: 100 }}>
              {
                avatarSrc ?  <Avatar
                src={avatarSrc || FileUploadPlaceholderSvg}
                sx={{ width: 112, height: 112, boxShadow: 1 }}
              /> : <FileUploadPlaceholderSvg />
              }
             
              <Tooltip title="Bild hochladen">
                <label htmlFor={`user-avatar-${index}`} style={{ position: "absolute", right: 35, bottom: 3 }}>
                  <input
                    id={`user-avatar-${index}`}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => onAvatarPick(e.target.files?.[0] || null)}
                  />
                  <Fab size="small" className="bxShadow circleRadius" component="span" sx={{ boxShadow: 2, backgroundColor: "#FFFFFF" }}>
                    <UploadFileSvg />
                  </Fab>
                </label>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
